import React from 'react';
import { Layout, Typography } from 'antd';
import Navbar from '../components/Navbar';
import AppFooter from '../components/Footer';
import './PrivacyPolicy.scss';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const PrivacyPolicy: React.FC = () => {
  return (
    <Layout className="privacy-layout">
      <Navbar />
      <Content className="privacy-content">
        <section className="privacy-section">
          <Title level={1} className="privacy-title">Privacy Policy</Title>
          <Paragraph className="privacy-date">Last Modified: May 5, 2025</Paragraph>

          <Title level={2} className="privacy-subtitle">1. Introduction</Title>
          <Paragraph>
            Replicats is committed to protecting your privacy. This Privacy Policy applies to all personal information
            collected when you interact with our website, use the Replicats Platform, and engage with our AI Investment
            Agents.
          </Paragraph>
          <Paragraph>
            This policy describes:
            <ul>
              <li>What information we collect and why</li>
              <li>How we use and protect your information</li>
              <li>Your rights regarding your personal information</li>
              <li>How you can contact us about privacy matters</li>
            </ul>
          </Paragraph>
          <Paragraph>
            "Personal Data" means any information relating to an identified or identifiable individual. If you don't agree
            with this Privacy Policy, please do not use our services.
          </Paragraph>
          <Paragraph>
            We may update this policy periodically. When we make significant changes, we'll notify you via email or through
            a notice on our website.
          </Paragraph>
          <Paragraph className="privacy-contact">
            For questions about this Privacy Policy or to exercise your privacy rights, please contact us at{' '}
            <Text strong>
              <a href="mailto:support@replicats.ai">support@replicats.ai</a>
            </Text>.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">2. Information Collection</Title>
          <Title level={3} className="privacy-subsubtitle">Information You Provide</Title>
          <Paragraph>
            We collect your wallet address when you access the Replicats Platform. This is necessary to link your portfolio
            and authenticate your access to our services.
          </Paragraph>
          <Paragraph>
            When you create an account or sign up for our services, we collect your email address for account management
            and communication purposes. If you connect through our third-party authentication provider, they may also
            collect your name and email address according to their{' '}
            <a href="https://dynamic.xyz/privacy" target="_blank" rel="noopener noreferrer" className="policy-link">
              privacy policy
            </a>.
          </Paragraph>
          <Paragraph>
            We also collect your investment preferences, risk tolerance, and portfolio customization choices. This
            information is essential for your AI Investment Agents to operate according to your specifications.
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Automatically Collected Information</Title>
          <Paragraph>
            When you visit our website or use our platform, we automatically collect technical information including:
            <ul>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Referring website</li>
              <li>Pages visited and features used</li>
              <li>Time spent on pages</li>
              <li>Device information</li>
            </ul>
          </Paragraph>
          <Paragraph>
            This information helps us understand how users interact with our platform, identify technical issues, and
            improve user experience. From time to time, we may engage third-party analytics services to help us analyze
            this usage data. These analytics providers are bound by their own privacy policies and are only authorized to
            use this information to provide analytics services to us.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">3. Use of Information</Title>
          <Paragraph>
            We use the information we collect to:
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Provide Our Services</Title>
          <Paragraph>
            Your wallet address and investment preferences allow us to authenticate you and customize your experience on
            the Replicats Platform. This information is essential for creating and managing your AI Investment Agents.
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Power Your AI Investment Agents</Title>
          <Paragraph>
            Our proprietary AI framework—utilizing deep learning, reinforcement learning, Graph Neural Networks (GNNs),
            Vector Error Correction Models (VECM), and Support Vector Machines (SVMs)—processes your investment
            preferences and portfolio data to make investment decisions aligned with your goals. Your data trains and
            customizes these AI agents according to your specific requirements and risk tolerance.
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Improve Our Platform</Title>
          <Paragraph>
            We analyze usage patterns and feedback to enhance our services, develop new features, and optimize the user
            experience. When we share statistical information about platform usage, we aggregate the data and do not
            identify individual users.
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Security and Compliance</Title>
          <Paragraph>
            We use your information to verify accounts, prevent fraud, and ensure platform security. We may also use this
            information to comply with legal obligations.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">4. Information Sharing</Title>
          <Paragraph>
            We may share your Personal Data with third-party service providers who help us deliver and improve our
            services. These service providers include:
            <ul>
              <li>Cloud infrastructure providers who host our platform</li>
              <li>Analytics providers who help us understand platform usage</li>
              <li>AI technology partners who support our investment agent functionality</li>
              <li>Payment processors who handle subscription transactions</li>
              <li>
                Authentication and wallet connection services, such as{' '}
                <a href="https://dynamic.xyz" target="_blank" rel="noopener noreferrer" className="policy-link">
                  dynamic.xyz
                </a>
              </li>
              <li>
                Blockchain integration services, such as{' '}
                <a href="https://crossmint.com" target="_blank" rel="noopener noreferrer" className="policy-link">
                  crossmint.com
                </a>
              </li>
              <li>Other third-party integrations we may engage from time to time to enhance our platform functionality</li>
            </ul>
          </Paragraph>
          <Paragraph>
            These service providers are contractually obligated to use your Personal Data only for the purposes of
            providing services to us and are required to maintain appropriate security measures to protect your
            information.
          </Paragraph>
          <Paragraph>
            We may also disclose your Personal Data if required by law, court order, or other legal process, or if we have
            a good faith belief that disclosure is necessary to protect our rights, investigate fraud, or protect the
            safety of our users or the public.
          </Paragraph>
          <Paragraph>
            We do not sell, rent, or lease your Personal Data to third parties.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">5. Data Security and Storage</Title>
          <Title level={3} className="privacy-subsubtitle">Data Security</Title>
          <Paragraph>
            We implement appropriate technical and organizational measures to protect your Personal Data against
            unauthorized access, accidental loss, destruction, or damage. These measures include:
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Access controls and authentication measures</li>
              <li>Regular security assessments and penetration testing</li>
              <li>Employee training on data protection practices</li>
              <li>Incident response procedures</li>
            </ul>
          </Paragraph>
          <Paragraph>
            Your data is stored on secure servers provided by{' '}
            <a
              href="https://cloud.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="policy-link"
            >
              Google Cloud Platform
            </a>{' '}
            in the United States. Google Cloud Platform maintains industry-leading security certifications and compliance
            standards, providing additional layers of protection for your information.
          </Paragraph>
          <Paragraph>
            While we work hard to protect your information, no method of transmission over the Internet or electronic
            storage is 100% secure. We cannot guarantee absolute security but continuously strive to enhance our security
            measures.
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Data Retention</Title>
          <Paragraph>
            We retain your Personal Data only for as long as necessary to fulfill the purposes for which it was collected,
            including legal, accounting, or reporting requirements. When determining retention periods, we consider various
            factors including the nature of the data, our ongoing relationship with you, and applicable legal
            requirements.
          </Paragraph>
          <Paragraph>
            When Personal Data is no longer necessary, we securely delete or anonymize it.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">6. Cookie Policy</Title>
          <Title level={3} className="privacy-subsubtitle">What Are Cookies</Title>
          <Paragraph>
            Cookies are small text files placed on your device when you visit our website. They help us recognize your
            device and remember certain information about your visit.
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Types of Cookies We Use</Title>
          <Paragraph>
            We use the following types of cookies on our website:
            <ul>
              <li>
                <Text strong>Essential Cookies:</Text> Required for the website to function properly. These enable basic
                functions like page navigation and access to secure areas of the website.
              </li>
              <li>
                <Text strong>Analytical/Performance Cookies:</Text> Help us understand how visitors interact with our
                website by collecting and reporting information anonymously. We use these to improve the way our website
                works.
              </li>
              <li>
                <Text strong>Functionality Cookies:</Text> Allow the website to remember choices you make and provide
                enhanced, personalized features.
              </li>
              <li>
                <Text strong>Targeting Cookies:</Text> Record your visit to our website, the pages you visit, and the links
                you follow. We use this information to make our website and advertising more relevant to your interests.
              </li>
            </ul>
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">Managing Cookies</Title>
          <Paragraph>
            Most web browsers allow you to control cookies through their settings. You can usually find these settings in
            the "Options" or "Preferences" menu of your browser. You can delete existing cookies, allow or block all
            cookies, or block cookies from particular sites.
          </Paragraph>
          <Paragraph>
            To learn more about cookies and how to manage them, visit{' '}
            <a
              href="https://www.allaboutcookies.org"
              target="_blank"
              rel="noopener noreferrer"
              className="policy-link"
            >
              www.allaboutcookies.org
            </a>{' '}
            or your browser's help section.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">7. User Rights</Title>
          <Paragraph>
            You have the following rights regarding your personal data:
            <ul>
              <li>
                <Text strong>Access and Portability:</Text> You can request a copy of the personal data we hold about you
                and information about how we process it.
              </li>
              <li>
                <Text strong>Correction:</Text> You can ask us to correct inaccurate or incomplete information about you.
              </li>
              <li>
                <Text strong>Deletion:</Text> You can request that we delete your personal data in certain circumstances,
                such as when the data is no longer necessary for the purposes for which it was collected.
              </li>
              <li>
                <Text strong>Objection and Restriction:</Text> You can object to our processing of your data or ask us to
                restrict processing in certain situations.
              </li>
              <li>
                <Text strong>Withdrawal of Consent:</Text> Where we process data based on your consent, you can withdraw
                that consent at any time.
              </li>
            </ul>
          </Paragraph>
          <Paragraph>
            To exercise these rights, please contact us at{' '}
            <Text strong>
              <a href="mailto:privacy@replicats.ai">privacy@replicats.ai</a>
            </Text>. We'll respond to your request within a reasonable timeframe. We may need to verify your identity
            before fulfilling your request.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">8. International Considerations</Title>
          <Paragraph>
            When we transfer your personal data from one country to another, we implement appropriate safeguards to ensure
            your data remains protected according to this Privacy Policy.
          </Paragraph>
          <Paragraph>
            We use our best efforts to comply with privacy regulations in our users' jurisdictions. However, as we continue
            to develop our platform, we may need to implement access restrictions in certain regions where we cannot yet
            ensure full compliance with local data protection requirements.
          </Paragraph>
          <Paragraph>
            If you would like more information about the safeguards we have in place for international transfers, please
            contact us at{' '}
            <Text strong>
              <a href="mailto:support@replicats.ai">support@replicats.ai</a>
            </Text>.
          </Paragraph>

          <Title level={2} className="privacy-subtitle">9. Additional Information</Title>
          <Title level={3} className="privacy-subsubtitle">Children's Privacy</Title>
          <Paragraph>
            Our services are not intended for or directed at children under 18 years of age. We do not knowingly collect
            personal information from children. If we become aware that we have collected personal data from a child
            without parental consent, we will take steps to delete that information.
          </Paragraph>
          <Title level={3} className="privacy-subsubtitle">No Selling of Personal Data</Title>
          <Paragraph>
            We do not sell your personal data to third parties under any circumstances.
          </Paragraph>
        </section>
      </Content>
      <AppFooter />
    </Layout>
  );
};

export default PrivacyPolicy;